// The clock behind the three curtain buttons.
//
// A press starts a timed run: publish the direction, wait the configured
// seconds, publish STOPCURTAIN. This module owns those timers.
//
// ── Why it is a module and not a hook ──────────────────────────────────────
// ⚠️ THE RUN MUST OUTLIVE THE SCREEN. A curtain takes up to twenty seconds and
// the operator can navigate away inside that — back to the room grid, into
// settings, anywhere. With the timer inside a component, unmounting cancels it
// and the STOP is never sent: the motor keeps running until the hardware's own
// limit, which is the failure this whole mechanism exists to prevent.
//
// Being module-level also means the grid card and the device screen read the
// same run. Two independent timers for one motor would drift, and the second
// STOP would arrive during a *later* run and cut it short.
//
// ── The three rules ────────────────────────────────────────────────────────
//
//   1. idle + a direction        -> publish it, start the clock
//   2. moving + the OTHER one    -> publish STOP, pause, publish the new one
//   3. moving + the SAME one     -> IGNORED. See below.
//
// ⚠️ RULE 3 IS NOT LAZINESS, IT IS THE FIRMWARE. On `OtaForTuya` a repeated
// direction is a STOP, not a restart:
//
//     OtaForTuya.ino:6065   if (CloseCurtainDone) {
//                             if (OpenCurtainDone) StartCurtainOpen();
//                             else                 StopCurtainOpen();   // <-- here
//
// So sending OPENCURTAIN to a curtain that is already opening halts the motor
// while this module goes on counting down, and the app would then publish its
// own STOP over an already-stopped curtain and report a completed run. Nothing
// errors; the curtain is simply half open. The button is disabled while its own
// direction runs, so "nothing happened" is visible rather than mysterious.

import { publishCommand } from '../utils/deviceController';
import { OPEN_CURTAIN, STOP_CURTAIN, CLOSE_CURTAIN, clampTravel } from './curtain';

export const IDLE = 'idle';
export const OPENING = 'opening';
export const CLOSING = 'closing';

/**
 * The pause between STOP and the opposite direction on a reversal.
 *
 * ⚠️ NOT DECORATION. Two publishes in the same tick arrive at the device
 * back to back, and on the relay firmware `StopCurtainOpen()` and
 * `StartCurtainClose()` drive two relays with no interlock between them — the
 * firmware says so itself (`OtaForTuya.ino:4758`: the gate has a hard interlock
 * inside `GateDrive()`, the curtain "relies on the caller having called
 * StopCurtainClose() first. That is caller discipline, not a guarantee").
 * Reversing a mains motor with no dwell is also how contacts weld.
 *
 * 300 ms: long enough to be a distinct message and for the contacts to open,
 * short enough that the reversal still reads as instant.
 */
export const REVERSE_GAP_MS = 300;

const DIRECTION_COMMAND = { [OPENING]: OPEN_CURTAIN, [CLOSING]: CLOSE_CURTAIN };

// uno -> { state, startsAt, endsAt, durationMs, startTimer, stopTimer }
const runs = new Map();
const listeners = new Set();

const emit = () => listeners.forEach((fn) => {
  try { fn(); } catch (e) { /* one bad subscriber must not stop the rest */ }
});

/** Subscribe to every curtain's run state. -> unsubscribe */
export function subscribeCurtains(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

const IDLE_STATE = { state: IDLE, startsAt: 0, endsAt: 0, durationMs: 0 };

/** What one curtain is doing right now. Never null. */
export function curtainState(uniqueNumber) {
  const r = runs.get(String(uniqueNumber ?? ''));
  if (!r) return IDLE_STATE;
  return {
    state: r.state, startsAt: r.startsAt, endsAt: r.endsAt, durationMs: r.durationMs,
  };
}

function clearTimers(rec) {
  if (!rec) return;
  if (rec.startTimer) clearTimeout(rec.startTimer);
  if (rec.stopTimer) clearTimeout(rec.stopTimer);
  rec.startTimer = null;
  rec.stopTimer = null;
}

/**
 * Stop now: publish STOPCURTAIN and drop the run.
 *
 * Publishes even when this app thinks the curtain is idle — the motor may have
 * been started from the wall switch or another phone, and a Stop button that
 * decides for itself that there is nothing to stop is the one thing it must
 * never do.
 */
export function stopCurtain(uniqueNumber, userId) {
  const uno = String(uniqueNumber ?? '');
  if (!uno) return;
  const rec = runs.get(uno);
  clearTimers(rec);
  runs.delete(uno);
  publishCommand(uno, STOP_CURTAIN, userId);
  emit();
}

// The end of a normal run. Separate from stopCurtain only so that a run which
// finished on its own is not indistinguishable from one somebody halted.
function finish(uno, userId) {
  const rec = runs.get(uno);
  clearTimers(rec);
  runs.delete(uno);
  publishCommand(uno, STOP_CURTAIN, userId);
  emit();
}

function begin(uniqueNumber, userId, direction, seconds) {
  const uno = String(uniqueNumber ?? '');
  if (!uno) return;
  const prev = runs.get(uno);

  // Rule 3 — already going this way. Doing nothing is the correct action.
  if (prev && prev.state === direction) return;

  const reversing = !!prev && prev.state !== IDLE;
  clearTimers(prev);

  // Rule 2 — stop first, and say so on the wire before the new direction goes
  // out. On `OtaForTuya` the direction command alone would also reverse, but
  // the DP-driven firmwares need the explicit stop, and one sequence that works
  // on all three beats a branch on a firmware the app cannot see.
  if (reversing) publishCommand(uno, STOP_CURTAIN, userId);

  const durationMs = clampTravel(seconds) * 1000;
  const delay = reversing ? REVERSE_GAP_MS : 0;
  const startsAt = Date.now() + delay;

  const rec = {
    state: direction,
    startsAt,
    endsAt: startsAt + durationMs,
    durationMs,
    startTimer: null,
    stopTimer: null,
  };
  runs.set(uno, rec);
  // Emitted before the publish so the UI turns over on the press rather than
  // after a round trip — the button has to feel connected to the motor.
  emit();

  const fire = () => {
    rec.startTimer = null;
    publishCommand(uno, DIRECTION_COMMAND[direction], userId);
    rec.stopTimer = setTimeout(() => finish(uno, userId), durationMs);
  };

  if (delay) rec.startTimer = setTimeout(fire, delay);
  else fire();
}

/** Run the curtain open for `seconds`, reversing a close if one is running. */
export const openCurtain = (uniqueNumber, userId, seconds) =>
  begin(uniqueNumber, userId, OPENING, seconds);

/** Run the curtain closed for `seconds`, reversing an open if one is running. */
export const closeCurtain = (uniqueNumber, userId, seconds) =>
  begin(uniqueNumber, userId, CLOSING, seconds);

/**
 * Drop every run without publishing anything.
 *
 * ⚠️ TESTS AND TEARDOWN ONLY. It abandons the timers, so a curtain mid-run is
 * left energised until the hardware's own limit catches it. Never call this to
 * "cancel" a run — that is `stopCurtain`.
 */
export function resetCurtainRuns() {
  runs.forEach(clearTimers);
  runs.clear();
  emit();
}
